import { motion } from "framer-motion";
import { ImSpinner8, ImConnection } from "react-icons/im";
import { MdOutlineSupport } from "react-icons/md";
export default function GlobalDataError({ retry = null, isLoading = false }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 bg-gradient-to-br from-red-50 to-rose-50 z-[9999] flex flex-col items-center justify-center p-4 sm:p-6 text-center"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNmZmYiLz48cGF0aCBkPSJNMCAwTDEwIDEwTTEwIDBMMCAxMCIgc3Ryb2tlPSIjMzg3ZTJjIiBzdHJva2Utd2lkdGg9IjEiLz48L3N2Zz4=')]"></div>
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center w-full max-w-2xl p-4">
        {/* Network icon */}
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-red-100 rounded-full opacity-75 animate-ping"></div>
          <svg
            className="relative w-16 h-16 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
            />
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-red-600">
          Application Configuration Error
        </h2>
        <div className="space-y-5">
          <div className="space-y-2 text-gray-600">
            <p>
              We couldn't load the required global settings needed to run this
              application.
            </p>
            <p className="font-medium text-red-500">
              Failed to load essential application data
            </p>
            <div className="flex items-start gap-2 p-3 mt-3 border rounded-lg bg-amber-50 border-amber-200">
              <ImConnection className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                This might be a temporary network issue or server problem.
              </p>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex flex-col justify-center w-full gap-3 sm:flex-row">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={retry}
              disabled={isLoading}
              className={`flex items-center justify-center gap-2 px-6 py-2 sm:py-3 rounded-lg ${
                isLoading
                  ? "bg-gray-200 text-gray-600"
                  : "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md hover:shadow-lg"
              } transition-all font-medium text-sm sm:text-base`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="w-4 h-4 text-white animate-spin sm:h-5 sm:w-5"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Connecting...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Try Again
                </>
              )}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium text-green-700 transition-all bg-white border-2 border-green-300 rounded-lg sm:py-3 hover:border-green-400 hover:text-green-800 sm:text-base"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Full Page Refresh
            </motion.button>
          </div>
        </div>

        {/* Support section */}
        <div className="w-full pt-4 mt-6 text-xs text-gray-600d sm:text-sm">
          <div className="flex items-center gap-3 px-6 py-4 bg-white border-t border-gray-200 shadow-sm">
            <MdOutlineSupport className="flex-shrink-0 text-xl text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">
                Still having trouble? Contact our support team.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
